import type {
  AccountingFrame,
  GameStateSnapshot,
  RankExpPoint,
  RankSubmissionDraft,
} from '@/domain/types'
import type { GamePreset } from '@/presets/types'

export const RANK_MINIMUM_DURATION_MS = 3_600_000
export const RANK_MAP_STALE_MS = 15_000

interface ExpAccumulator {
  start: RankExpPoint | null
  end: RankExpPoint | null
  last: RankExpPoint | null
  gain: number
  trustedDurationMs: number
}

interface MesosAccumulator {
  startValue: number | null
  endValue: number | null
  lastValue: number | null
  gain: number
  trustedDurationMs: number
}

interface ActiveSegment {
  mapName: string
  levelBucket: number
  startedAt: number
  lastContinuousAt: number
  captureGeneration: number
  exp: ExpAccumulator
  mesos: MesosAccumulator
}

const emptyExp = (): ExpAccumulator => ({ start: null, end: null, last: null, gain: 0, trustedDurationMs: 0 })
const emptyMesos = (): MesosAccumulator => ({ startValue: null, endValue: null, lastValue: null, gain: 0, trustedDurationMs: 0 })

export function rankLevelBucket(level: number): number {
  return Math.floor(level / 5) * 5
}

export function normalizeRankMapName(value: string): string {
  return value
    .normalize('NFKC')
    .replace(/[\u0000-\u001f\u007f-\u009f]+/gu, ' ')
    .replace(/\s+/gu, ' ')
    .trim()
}

export class EfficiencySegmentTracker {
  private segment: ActiveSegment | null = null

  constructor(private readonly preset: GamePreset) {}

  reset(): void {
    this.segment = null
  }

  ingest(state: GameStateSnapshot, accounting: AccountingFrame): RankSubmissionDraft | null {
    const mapName = state.mapName ? normalizeRankMapName(state.mapName) : ''
    const mapObservedAt = state.observedAt?.mapName ?? null
    const level = state.level

    if (!mapName || mapObservedAt === null || state.capturedAt - mapObservedAt > RANK_MAP_STALE_MS || level === null) {
      return this.finish(this.segment?.lastContinuousAt ?? state.capturedAt)
    }

    const levelBucket = rankLevelBucket(level)
    let completed: RankSubmissionDraft | null = null
    const startsNewSegment = !this.segment
      || this.segment.mapName !== mapName
      || this.segment.levelBucket !== levelBucket
    if (startsNewSegment) {
      completed = this.finish(this.segment?.lastContinuousAt ?? state.capturedAt)
      this.segment = {
        mapName,
        levelBucket,
        startedAt: state.capturedAt,
        lastContinuousAt: state.capturedAt,
        captureGeneration: accounting.captureGeneration,
        exp: emptyExp(),
        mesos: emptyMesos(),
      }
    } else {
      this.segment!.lastContinuousAt = state.capturedAt
    }

    if (this.segment!.captureGeneration !== accounting.captureGeneration) {
      this.segment!.captureGeneration = accounting.captureGeneration
      this.segment!.exp.last = null
      this.segment!.mesos.lastValue = null
    }
    this.observeExp(accounting)
    this.observeMesos(accounting)
    return completed
  }

  finish(now = Date.now()): RankSubmissionDraft | null {
    const segment = this.segment
    this.segment = null
    if (!segment?.exp.start || !segment.exp.end || segment.exp.trustedDurationMs < RANK_MINIMUM_DURATION_MS) return null

    const endedAt = Math.max(segment.startedAt, Math.min(now, segment.lastContinuousAt))
    const mesos = segment.mesos
    return {
      submissionId: crypto.randomUUID(),
      presetId: this.preset.id,
      presetVersion: this.preset.version,
      mapName: segment.mapName,
      levelBucket: segment.levelBucket,
      startedAt: segment.startedAt,
      endedAt,
      exp: {
        gain: segment.exp.gain,
        trustedDurationMs: segment.exp.trustedDurationMs,
        start: segment.exp.start,
        end: segment.exp.end,
      },
      ...(mesos.startValue !== null
        && mesos.endValue !== null
        && mesos.gain >= 0
        && mesos.trustedDurationMs >= RANK_MINIMUM_DURATION_MS
        ? {
            mesos: {
              gain: mesos.gain,
              trustedDurationMs: mesos.trustedDurationMs,
              startValue: mesos.startValue,
              endValue: mesos.endValue,
            },
          }
        : {}),
    }
  }

  private observeExp(accounting: AccountingFrame): void {
    if (!this.segment) return
    const update = accounting.exp
    const metric = this.segment.exp
    if (update.continuity === 'ignored' || update.continuity === 'pending') return
    if (update.continuity === 'unavailable' || update.continuity === 'discontinuous') {
      metric.last = update.end
      if (update.end) {
        metric.start ??= update.end
        metric.end = update.end
      }
      return
    }
    if (!update.exact || !update.end) return
    if (!metric.last || update.continuity === 'baseline' || !update.start) {
      metric.start ??= update.end
      metric.end = update.end
      metric.last = update.end
      return
    }
    if (!this.sameExpPoint(metric.last, update.start) || update.delta === null || update.delta < 0) {
      metric.last = update.end
      metric.end = update.end
      return
    }
    metric.gain += update.delta
    metric.trustedDurationMs += Math.max(0, update.trustedIntervalMs)
    metric.last = update.end
    metric.end = update.end
  }

  private observeMesos(accounting: AccountingFrame): void {
    if (!this.segment) return
    const update = accounting.mesos
    const metric = this.segment.mesos
    if (update.continuity === 'ignored' || update.continuity === 'pending') return
    if (update.continuity === 'unavailable' || update.continuity === 'discontinuous') {
      metric.lastValue = update.endValue
      if (update.endValue !== null) {
        metric.startValue ??= update.endValue
        metric.endValue = update.endValue
      }
      return
    }
    if (update.endValue === null) return
    if (metric.lastValue === null || update.continuity === 'baseline' || update.startValue === null) {
      metric.startValue ??= update.endValue
      metric.endValue = update.endValue
      metric.lastValue = update.endValue
      return
    }
    if (metric.lastValue !== update.startValue || update.delta === null) {
      metric.lastValue = update.endValue
      metric.endValue = update.endValue
      return
    }
    if (update.delta >= 0) {
      metric.gain += update.delta
      metric.trustedDurationMs += Math.max(0, update.trustedIntervalMs)
    }
    metric.lastValue = update.endValue
    metric.endValue = update.endValue
  }

  private sameExpPoint(left: RankExpPoint, right: RankExpPoint): boolean {
    return left.level === right.level && left.absolute === right.absolute
  }
}
